import React, { useState, useCallback } from "react";
import { Card, Button } from "../../common";
import FileDropArea from "../../common/FileDropArea";
import {
  DrawingConversionRequest,
  DrawingOutputFormat,
  DRAWING_INPUT_FORMATS,
  DRAWING_OUTPUT_FORMATS,
  isValidInputFormat,
  getFileExtension,
} from "../../../types/drawingTypes";

//#region 型定義
interface DrawingFormProps {
  onSubmit: (request: DrawingConversionRequest) => void;
  onFileSelect: (file: File | null) => void;
  onReset: () => void;
  isLoading: boolean;
}

interface FormData {
  outputFormat: DrawingOutputFormat;
  selectedVersion?: string;
  quality: number;
}
//#endregion

/**
 * 図面変換フォームコンポーネント
 * @description ファイルの選択と変換パラメータの設定を行うフォーム
 */
export const DrawingForm: React.FC<DrawingFormProps> = ({
  onSubmit,
  onFileSelect,
  onReset,
  isLoading,
}) => {
  //#region 状態管理
  const initialFormData: FormData = {
    outputFormat: "pdf",
    quality: 90,
  };

  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [fileError, setFileError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  //#endregion

  //#region ファイル検証処理
  const validateFile = useCallback((file: File): boolean => {
    const extension = getFileExtension(file.name);
    if (!isValidInputFormat(extension)) {
      const supportedFormats = DRAWING_INPUT_FORMATS.map((f) => f.label).join(", ");
      setFileError(`サポートされているファイル形式: ${supportedFormats}`);
      return false;
    }

    const MAX_FILE_SIZE = 100 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      setFileError("ファイルサイズは100MB以下にしてください");
      return false;
    }

    return true;
  }, []);
  //#endregion

  //#region ファイル選択処理
  const handleFileDrop = useCallback((files: FileList) => {
    const file = files[0];
    if (file && validateFile(file)) {
      setSelectedFile(file);
      setFileError(null);
      onFileSelect(file);
    } else {
      setSelectedFile(null);
      onFileSelect(null);
    }
  }, [validateFile, onFileSelect]);
  //#endregion

  //#region フォームリセット処理
  const handleReset = useCallback(() => {
    setFormData(initialFormData);
    setSelectedFile(null);
    setFileError(null);
    onFileSelect(null);
    onReset();
  }, [onFileSelect, onReset]);
  //#endregion

  //#region フォーム値変更処理
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const newData = { ...prev };
      if (name === "outputFormat") {
        newData.outputFormat = value as DrawingOutputFormat;
        if (value !== "dwg" && value !== "dxf") {
          delete newData.selectedVersion;
        }
      } else if (name === "selectedVersion") {
        newData.selectedVersion = value;
      } else if (name === "quality") {
        newData.quality = Number(value);
      }
      return newData;
    });
  }, []);
  //#endregion

  //#region フォーム送信処理
  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile) {
      setFileError("ファイルを選択してください");
      return;
    }

    const outputFilename = `${selectedFile.name.split(".")[0]}.${formData.outputFormat.toLowerCase()}`;
    const request: DrawingConversionRequest = {
      conversionParameters: {
        inputFilePath: "", // APIクライアントで設定される
        outputFormat: formData.outputFormat,
        outputFilePath: `C:\\Temp\\Output\\${outputFilename}`,
        quality: formData.quality,
        version: formData.selectedVersion,
      },
    };

    onSubmit(request);
  }, [selectedFile, formData, onSubmit]);
  //#endregion

  //#region UIレンダリング
  const selectedFormatInfo = DRAWING_OUTPUT_FORMATS.find((format) => format.value === formData.outputFormat);

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">図面ファイルを選択</label>
        <div className="w-full">
          <FileDropArea
            onDrop={handleFileDrop}
            error={fileError}
            accept={DRAWING_INPUT_FORMATS.map((f) => `.${f.value}`).join(",")}
            customMessage={{
              title: "図面ファイルを選択",
              description: "ファイルをドラッグ＆ドロップまたはクリック",
            }}
          />
        </div>
        {selectedFile && (
          <p className="text-xs text-gray-500 mt-1 break-all">選択中: {selectedFile.name}</p>
        )}
      </div>

      <div className="space-y-3 max-h-[calc(100vh-450px)] overflow-y-auto px-1">
        <div>
          <label htmlFor="outputFormat" className="block text-sm font-medium text-gray-700 mb-1">出力形式</label>
          <select
            id="outputFormat"
            name="outputFormat"
            value={formData.outputFormat}
            onChange={handleInputChange}
            className="block w-full h-9 px-2 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
          >
            {DRAWING_OUTPUT_FORMATS.map((format) => (
              <option key={format.value} value={format.value}>{format.label}</option>
            ))}
          </select>
        </div>

        {selectedFormatInfo?.versions && (
          <div>
            <label htmlFor="selectedVersion" className="block text-sm font-medium text-gray-700 mb-1">バージョン</label>
            <select
              id="selectedVersion"
              name="selectedVersion"
              value={formData.selectedVersion || ""}
              onChange={handleInputChange}
              className="block w-full h-9 px-2 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
            >
              <option value="">バージョンを選択</option>
              {selectedFormatInfo.versions.map((version) => (
                <option key={version} value={version}>{version}</option>
              ))}
            </select>
          </div>
        )}

        {(formData.outputFormat === "pdf" || formData.outputFormat === "svg") && (
          <div>
            <label htmlFor="quality" className="block text-sm font-medium text-gray-700 mb-1">品質 (1-100)</label>
            <input
              type="number"
              id="quality"
              name="quality"
              min="1"
              max="100"
              value={formData.quality}
              onChange={handleInputChange}
              className="block w-full h-9 px-2 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
            />
          </div>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-2 pt-2">
        <Button
          type="submit"
          variant="primary"
          isLoading={isLoading}
          disabled={!selectedFile || isLoading || (selectedFormatInfo?.versions && !formData.selectedVersion)}
          className="w-full sm:flex-1 h-9 text-sm"
        >
          {isLoading ? "変換中..." : "変換開始"}
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={handleReset}
          disabled={isLoading}
          className="w-full sm:flex-1 h-9 text-sm"
        >
          リセット
        </Button>
      </div>
    </form>
  );
  //#endregion
};

export default DrawingForm;